/* eslint-disable import/extensions */
/* eslint-disable no-param-reassign */
import { errorLog } from '@bit/gerenciatd.utils.log-utils';
import {
  Sonda,
  WSElementType,
  MethodType,
  Request as RequestInterface,
  MockStructure as MockStructureInterface,
  ENV_NAME,
} from '@bit/gerenciatd.utils.types';

const DefaultHeaders = {
  timeout: 50000,
};

export class MockStructure implements MockStructureInterface {
  statusCode: number;

  delay: number;

  rawResponse: string;

  constructor(statusCode: number, delay: number, rawResponse: string) {
    this.statusCode = statusCode;
    this.delay = delay;
    this.rawResponse = rawResponse;
  }
}

export const getMockedInfo = (
  methodName: string,
  name: string
): MockStructure => {
  if (
    name !== ENV_NAME.PROD
    && sessionStorage
    && sessionStorage.getItem(methodName)
  ) {
    const sessionMockInfo = JSON.parse(sessionStorage.getItem(methodName));
    return new MockStructure(
      sessionMockInfo.statusCode,
      sessionMockInfo.delay,
      JSON.stringify(sessionMockInfo.rawResponse)
    );
  }
  return null;
};

class BasicResponse {
  statusCode: number;

  statusText: string;

  constructor(statusCode: number, statusText: string) {
    this.statusCode = statusCode;
    this.statusText = statusText;
  }
}

/* AUXILIAR FUNCTIONS clean for GET */
export const cleanResponse = (response: any): any => {
  let finalResponse = response;

  // Para casos en que devuelve un html o un string que no es un json
  if (typeof response === 'string' && finalResponse) {
    finalResponse = finalResponse.trim();
    try {
      finalResponse = JSON.parse(finalResponse);
    } catch (err) {
      finalResponse = response;
    }
  }

  return finalResponse;
}

export const appendStatusVarsIfNeeded = (data: any, basicResponse: BasicResponse): any => {
  const res = { ...data };

  if (!res.status) {
    res.status = basicResponse.statusCode;
  }

  if (!res.statusText) {
    res.statusText = basicResponse.statusText;
  }

  return res;
}

export const setRequestHeaders = (headers: any, xhr: any) => {
  const headerKeys = Object.keys(headers);

  headerKeys.forEach((headerKey) => {
    const value = headers[headerKey];

    if (headerKey === 'withCredentials') {
      xhr.withCredentials = value;
    } else if (headerKey === 'timeout') {
      xhr.timeout = value;
    } else {
      xhr.setRequestHeader(`${headerKey}`, value);
    }
  });
}

export default class Request implements RequestInterface {
  method: MethodType;

  endpoint: string;

  data: any;

  onError: Function;

  onTimeout: Function;

  onSuccess: Function;

  onRedirect: Function;

  onFetching: Function;

  sonda: Sonda;

  redirectAsReject: Boolean;

  headers: any;

  dataStringify: Boolean;

  mockInfo: MockStructure;

  onLoadXhr: Function;

  /** Realiza una petición sondada o no.
   * @param {MethodType} method tipo de peticion
   * @param {String} endpoint url del servicio al que se llama
   * @param {Object} data conjunto de datos que se envían al servicio
   * @param {Function} onError función adicional que se ejecuta dado un error
   * @param {Function} onTimeout función adicional que se ejecuta dado un timeout
   * @param {Function} onSuccess función adicional que se ejecuta
   *  cuando se obtiene respuesta del servicio
   * @param {Function} onRedirect función adicional que se ejecuta
   *  cuando se esta realizando una redirección
   * @param {Function} onFetching función adicional que se ejecuta
   * cuando se esta esperando respuesta
   * @param {Sonda} sonda si se recibe, el servicio se auto-sondará
   *  al pasar por los distintos eventos.
   * @param {Boolean} redirectAsReject si se recibe, una redirección se resolverá
   *  como un reject en vez de un resolve
   * @param {any} headers cabeceras a utilizar en la petición
   * @param {Boolean} dataStringify cuando es true, se transforma la información
   *  a string antes de enviarse al servicio
   * @param {MockStructure} mockInfo estructura con la información
   *  necesaria para mockear la respuesta de la promise
   */
  constructor({
    method,
    endpoint,
    redirectAsReject = true,
    headers,
    dataStringify = true,
    data,
    onError,
    onTimeout,
    onSuccess,
    onRedirect,
    onFetching,
    sonda,
    mockInfo,
  }: {
    endpoint: string;
    headers: any;
    method?: MethodType;
    redirectAsReject?: Boolean;
    dataStringify?: Boolean;
    data?: any;
    onError?: Function;
    onTimeout?: Function;
    onSuccess?: Function;
    onRedirect?: Function;
    onFetching?: Function;
    sonda?: Sonda;
    mockInfo?: MockStructure;
  }) {
    this.method = method || MethodType.GET;
    this.endpoint = endpoint;
    this.data = data;
    this.onError = onError;
    this.onTimeout = onTimeout;
    this.onSuccess = onSuccess;
    this.onRedirect = onRedirect;
    this.onFetching = onFetching;
    this.sonda = sonda;
    this.redirectAsReject = redirectAsReject;
    this.headers = {
      ...DefaultHeaders,
      ...headers,
    };
    this.dataStringify = dataStringify;
    this.mockInfo = mockInfo;
    /* CHECKINGS */
    if (!endpoint) {
      errorLog('[Request]: endpoint prop can not be undefined');
    }
  }

  errorHandler(
    currentXhr: any = {
      status: 500,
      statusText: 'Generic Error',
    }
  ): any {
    let onErrorObj = currentXhr;

    if (this.onError && currentXhr) {
      const onErrorAux = this.onError(currentXhr);

      if (onErrorAux) {
        onErrorObj = onErrorAux;
      }
    }

    return onErrorObj;
  }

  // doing onFetchings operations
  executeOnFetching(xhr: any) {
    // use sonda object
    if (this.sonda) {
      this.sonda.startWS();
    }
    // custom onFetching
    if (this.onFetching) {
      try {
        this.onFetching(xhr);
      } catch (err) {
        if (this.sonda) {
          this.sonda.errorJs(err.toString());
        }
        errorLog(
          `[Request]: Service:${this.sonda
            && this.sonda
              .eventName} onFetching handler does not work properly ${err.toString()}`
        );
      }
    }
  }

  // doing onSuccess things
  executeOnSuccess(xhr: any, response: any): any {
    let onSuccessObj = null;

    // tryng using sonda
    if (this.sonda) {
      this.sonda.endWS(WSElementType.OK);
    }
    // executing custom onSuccess
    if (this.onSuccess) {
      try {
        onSuccessObj = this.onSuccess(xhr);
      } catch (err) {
        if (this.sonda) {
          this.sonda.errorJs(err.toString());
        }
        errorLog(
          `[Request]: Service:${this.sonda
            && this.sonda
              .eventName} onSuccess handler does not work properly ${err.toString()}`
        );
      }
    }
    const res = onSuccessObj || { response };

    return res;
  }

  executeOnRedirect(xhr: any, response: any): any {
    let onRedirectObj = null;
    // tryng using sonda
    if (this.sonda) {
      this.sonda.endWS(WSElementType.OK);
    }

    if (this.onRedirect) {
      try {
        onRedirectObj = this.onRedirect(xhr);
      } catch (err) {
        if (this.sonda) {
          this.sonda.errorJs(err.toString());
        }
        errorLog(
          `[Request]: Service:${this.sonda
            && this.sonda
              .eventName} onRedirect handler does not work properly ${err.toString()}`
        );
      }
    }
    const res = onRedirectObj || { response };

    return res;
  }

  onTimeoutXHR(xhr: any, headers: any, reject: Function) {
    let onTimeoutObj = null;
    const statusText = `Timeout Error${
      headers && headers.timeout ? `: ${headers.timeout}ms` : ''
    }`;

    const data = this.errorHandler({
      status: 500,
      statusText,
    });

    // tryng using sonda
    if (this.sonda) {
      this.sonda.endWS(WSElementType.ERROR_TIMEOUT, data);
    }

    // executing custom onTimeout
    if (this.onTimeout) {
      try {
        onTimeoutObj = this.onTimeout(xhr);
      } catch (err) {
        if (this.sonda) {
          this.sonda.errorJs(err.toString());
        }
        errorLog(
          `[Request]: Service:${this.sonda
            && this.sonda
              .eventName} onTimeout handler does not work properly ${err.toString()}`
        );
      }
    }

    const res = onTimeoutObj || data;

    reject(res);
  }

  // ONTIMEOUT
  executeOnTimeout(xhr: any, headers: any, reject: Function) {
    // ONTIMEOUT
    xhr.ontimeout = () => {
      this.onTimeoutXHR(xhr, headers, reject);
    };
  }

  onErrorXHR(xhr: any, reject: Function) {
    let onErrorObj = null;

    const statusText = 'Network Error';

    const data = this.errorHandler({
      ...xhr,
      status: 500,
      statusText,
    });
    // tryng using sonda
    if (this.sonda) {
      this.sonda.endWS(WSElementType.ERROR, data);
    }

    // onError handler
    if (this.onError) {
      try {
        onErrorObj = this.onError(xhr);
      } catch (err) {
        if (this.sonda) {
          this.sonda.errorJs(err.toString());
        }
        console.log(
          `[Request]: Service:${this.sonda
            && this.sonda
              .eventName} onError handler does not work properlly ${err.toString()}`
        );
      }
    }

    const res = onErrorObj || data;

    reject(res);
  }

  // ONERROR
  executeOnError(xhr: any, reject: Function) {
    xhr.onerror = () => {
      this.onErrorXHR(xhr, reject);
    };
  }

  onLoadXHR(xhr: any, resolve: Function, reject: Function) {
    // Analizamos la reponse
    const currentStatus = xhr.status;
    const myResponse = cleanResponse(xhr.response);

    if (currentStatus >= 200 && currentStatus < 300) {
      // check if response is well formed
      let data = this.executeOnSuccess(xhr, myResponse);

      // if we have on onSuccess we dont append those
      if (!this.onSuccess) {
        data = appendStatusVarsIfNeeded(
          data,
          new BasicResponse(currentStatus, xhr.statusText)
        );
      }

      resolve(data);
    } else if (currentStatus >= 300 && currentStatus < 400) {
      // FIRST OF ALL WE need to detect redirection kind
      // actually service works fine
      let data = this.executeOnRedirect(xhr, myResponse);
      // if we have on onRedirect we dont append those
      if (!this.onRedirect) {
        data = appendStatusVarsIfNeeded(
          data,
          new BasicResponse(currentStatus, 'Redirection')
        );
      }

      // here should be a redirection normalizer (TODO)
      // to front developpers
      if (this.redirectAsReject) {
        reject(data);
      } else {
        resolve(data);
      }
    } else {
      const data = this.errorHandler({
        ...xhr,
        status: currentStatus,
        statusText: xhr.statusText,
        response: xhr && xhr.response ? xhr.response : null,
      });

      if (this.sonda) {
        this.sonda.endWS(WSElementType.ERROR, data);
      }
      reject(data);
    }
  }

  // onLoad xhr
  executeOnLoad(xhr: any, resolve: Function, reject: Function) {
    xhr.onload = () => {
      this.onLoadXHR(xhr, resolve, reject);
    };
  }

  // sending info
  sendXhrInfo(xhr: any) {
    // SEND INFO
    if (this.data) {
      if (this.dataStringify) {
        xhr.send(JSON.stringify(this.data));
      } else {
        xhr.send(this.data);
      }
    } else {
      xhr.send();
    }
  }

  executeMock(mockInfo: MockStructure, resolve: Function, reject: Function) {
    const mockedXHR = {
      status: mockInfo.statusCode,
      statusText: 'Mocked Request',
      response: mockInfo.rawResponse,
    };
    this.executeOnFetching(mockedXHR);
    const finalDelay = this.headers.timeout <= mockInfo.delay
      ? this.headers.timeout
      : mockInfo.delay;

    setTimeout(() => {
      if (mockInfo.statusCode >= 500) {
        this.onErrorXHR(mockedXHR, reject);
      } else if (mockInfo.delay >= this.headers.timeout) {
        this.onTimeoutXHR(mockedXHR, this.headers, reject);
      } else {
        this.onLoadXHR(mockedXHR, resolve, reject);
      }
    }, finalDelay || 1000);
  }

  /* MAIN */
  makeRequest(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.mockInfo) {
        const xhr = new XMLHttpRequest();

        xhr.open(this.method, this.endpoint, true);
        // apply headers to this xhr
        setRequestHeaders(this.headers, xhr);
        this.executeOnFetching(xhr);
        // lets see many events
        // ontimeout
        this.executeOnTimeout(xhr, this.headers, reject);
        // onerror
        this.executeOnError(xhr, reject);
        //  onload
        this.executeOnLoad(xhr, resolve, reject);
        // sending info
        this.sendXhrInfo(xhr);
      } else {
        this.executeMock(this.mockInfo, resolve, reject);
      }
    })
      .then((res) => {
        // normalizing depends on status?
        return res;
      })
      .catch((err) => {
        console.log(err);
        throw err;
      });
  }
}
